import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { API } from "../../helpers/constants";
import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CUSTOMERS } from "../../modules/customers/locales/namespaces";
import useUserData from "../../hooks/user/useUserData";
import { getRouteUrl } from "../../helpers/getRouteUrl";
import { useHistory } from "react-router-dom";
import {
  handleAxiosErrors,
  logoutUnauthorizedUser,
} from "../../helpers/errorHandling";
import { enqueueSnackbar } from "notistack";
import useUserAction from "../../hooks/user/useUserAction";
import { ROUTE_PAGE_LOGIN } from "../../routers/routes";
import { showPopup } from "../../../src/components/globalPopup/globalPopupService";

const useSignUpForm = ({
  customer,
  onCheckPhone,
  showPhoneInput,
}) => {
  const { userLogout } = useUserAction();
  const { token, isLoggedIn } = useUserData();
  const history = useHistory();
  const { t } = useTranslation(CUSTOMERS);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);

  // Helper function to remove dial code prefix from phone number
  const removeDialCodePrefix = (phoneNumber) => {
    if (!selectedCountryCode) {
      return phoneNumber;
    }

    // Remove the +dialCode prefix (e.g., "+30" -> "")
    return phoneNumber.replace(`+${selectedCountryCode}`, "");
  };

  const validationSchema = useMemo(
    () =>
      yup.object({
        password: yup
          .string()
          .required(t("Required"))
          .matches(
            /^.{60}$|(^((?=.*?[A-Z])(?=.*?[a-z])(?=.*?\d)|(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[^a-zA-Z\d])|(?=.*?[A-Z])(?=.*?\d)(?=.*?[^a-zA-Z\d])|(?=.*?[a-z])(?=.*?\d)(?=.*?[^a-zA-Z\d])).{7,16}$)/,
            t(
              "Password must be between 7-16 latin characters and contain at least 3 of the following. -Upper case character -Lower case character -Numeric digit -Special character",
            ),
          ),
        confirmPassword: yup
          .string()
          .required(t("Required"))
          .oneOf([yup.ref("password"), null], t("Passwords must match")),
        email: yup
          .string()
          .required(t("Required"))
          .email(t("Enter a valid email")),
        phone: showPhoneInput
          ? yup.string().required(t("Required"))
          : yup.string(),
      }),
    [showPhoneInput, t],
  );
  const onSubmit = async (values) => {
    try {
      let phoneData = null;

      // Check phone if required
      if (showPhoneInput) {
        const phoneResponse = await onCheckPhone(
          values.email,
          removeDialCodePrefix(values.phone),
          selectedCountryCode,
        );

        if (!phoneResponse?.validation) {
          showPopup({
            title: "",
            message: t(
              "Invalid phone number. Please check the phone number and country code.",
            ),
            onConfirm: () => {},
            ok: t("Close"),
          });
          return;
        }

        phoneData = {
          MobilePhone: removeDialCodePrefix(values.phone),
          Code: selectedCountryCode,
          CustName: phoneResponse?.CustName,
        };
      }

      // Prepare registration data
      const registrationData = {
        password: values.password,
        email: values.email,
        isActive: values.isActive,
        ...(phoneData && phoneData),
      };

      // Proceed with registration
      await axios({
        method: "post",
        data: registrationData,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        url: `${API}/registration`,
      });

      // Registration successful
      showPopup({
        title: "",
        message: t(
          "Your registration is complete! Check your email and click the activation link to access your account.",
        ),
        onConfirm: () => {},
      });
      history.push(getRouteUrl(ROUTE_PAGE_LOGIN));
    } catch (error) {
      console.error("Registration error:", error);
      showPopup({
        title: "",
        message: t("A user already exists with that email!"),
        onConfirm: () => {},
      });
      logoutUnauthorizedUser(error, isLoggedIn, userLogout);
    }
  };

  const formik = useFormik({
    initialValues: {
      isActive: "0",
      password: customer?.password || "",
      confirmPassword: customer?.password || "",
      email: customer?.email || "",
      phone: "",
    },
    validationSchema: validationSchema,
    onSubmit: onSubmit,
    enableReinitialize: true,
  });

  // Update validation schema when showPhoneInput changes
  useEffect(() => {
    if (!showPhoneInput) {
      formik.setFieldValue("phone", "");
    }
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPhoneInput]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  return {
    formik,
    showPassword,
    handleClickShowPassword,
    handleMouseDownPassword,
    selectedCountryCode,
    setSelectedCountryCode,
  };
};

export default useSignUpForm;
